import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import RoomDayColumn from './roomDayColumn';
import { useAgendaBlocks } from '../hooks/useAgendaBlocks';
import { useCalendarAppointments } from '../hooks/useCalendarAppointments';

// Um bloqueio "Toda a clínica" não tem sala nem profissional: vale para todas
// as salas, então precisa aparecer em cada coluna.
const isClinicWideBlock = (block) => !block.roomId && !block.professionalId;

function RoomsDayView({ date, rooms, roomLabel, onEventClick, onDateClick, filterAppointments }) {
  // Uma única busca da lista geral de bloqueios (mesmo endpoint da visão
  // semana), distribuída às colunas — evita N requisições por sala e garante
  // que os bloqueios "Toda a clínica" apareçam em todas.
  const { agendaBlocks } = useAgendaBlocks();
  // Mesma ideia dos bloqueios para as consultas: uma busca da lista geral do
  // calendário distribuída às colunas, em vez de uma requisição por sala.
  const { appointments } = useCalendarAppointments();

  const appointmentsByRoom = useMemo(() => {
    const visibleAppointments = filterAppointments(appointments);
    const map = new Map();
    rooms.forEach((room) => {
      map.set(
        room.id,
        visibleAppointments.filter((appointment) => String(appointment.roomId) === String(room.id)),
      );
    });
    return map;
  }, [appointments, rooms, filterAppointments]);

  const blocksByRoom = useMemo(() => {
    const clinicWide = agendaBlocks.filter(isClinicWideBlock);
    const map = new Map();
    rooms.forEach((room) => {
      const roomBlocks = agendaBlocks.filter((block) => String(block.roomId) === String(room.id));
      map.set(room.id, [...roomBlocks, ...clinicWide]);
    });
    return map;
  }, [agendaBlocks, rooms]);

  if (rooms.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {roomLabel.emptyMessage}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
      {rooms.map((room) => (
        <RoomDayColumn
          key={`${room.id}-${dayjs(date).format('YYYY-MM-DD')}`}
          room={room}
          date={date}
          appointments={appointmentsByRoom.get(room.id) ?? []}
          agendaBlocks={blocksByRoom.get(room.id) ?? []}
          onEventClick={onEventClick}
          onDateClick={onDateClick}
        />
      ))}
    </Box>
  );
}

RoomsDayView.propTypes = {
  date: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Date), PropTypes.string])
    .isRequired,
  rooms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
    }),
  ).isRequired,
  roomLabel: PropTypes.shape({
    emptyMessage: PropTypes.string,
  }).isRequired,
  onEventClick: PropTypes.func.isRequired,
  onDateClick: PropTypes.func.isRequired,
  filterAppointments: PropTypes.func,
};

RoomsDayView.defaultProps = {
  filterAppointments: (appointments) => appointments,
};

export default RoomsDayView;
